import { COMPLIANCE_DOCUMENT_CONTENT_TYPES } from '@chukua/common';
import { Button, Form, Modal, Upload } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { UploadCloud } from 'lucide-react';
import { ComplianceDocumentFormFields } from '../../../shared/features/compliance-documents/components/ComplianceDocumentFormFields';
import type { ComplianceDocumentEntry, ComplianceDocumentView } from '../../../shared/features/compliance-documents/policies/compliance-documents';
import { useToast } from '../../../shared/hooks/use-toast';
import type { UploadDriverDocumentInput } from '../hooks/use-driver-documents';

interface UploadFormValues {
  number: string;
  issuer: string;
  issueDate: Dayjs;
  expiryDate: Dayjs;
}

function toFormValues(current: ComplianceDocumentEntry): UploadFormValues {
  return {
    number: current.number,
    issuer: current.issuer,
    issueDate: dayjs(current.issueDate),
    expiryDate: dayjs(current.expiryDate),
  };
}

interface DriverDocumentUploadModalProps {
  open: boolean;
  /** The document type being added to or renewed — its name is shown, but never changed here. */
  documentType: ComplianceDocumentView | null;
  uploading: boolean;
  onUpload: (input: UploadDriverDocumentInput, isRenewal: boolean) => Promise<boolean>;
  onClose: () => void;
}

function isAllowedContentType(type: string): boolean {
  return (COMPLIANCE_DOCUMENT_CONTENT_TYPES as readonly string[]).includes(type);
}

/**
 * Attaches the first document for a type, or renews it — always a new row, never an edit of the
 * one it supersedes. Drives the three-call sequence (presign, PUT, confirm) — `onUpload` from
 * `useDriverDetail` does the actual calls. A renewal pre-fills from the document it supersedes,
 * since it's usually the same issuer and number with a later expiry.
 */
export function DriverDocumentUploadModal({ open, documentType, uploading, onUpload, onClose }: DriverDocumentUploadModalProps) {
  const [form] = Form.useForm<UploadFormValues>();
  const toast = useToast();
  const isRenewal = Boolean(documentType?.current);
  const initialValues = documentType?.current ? toFormValues(documentType.current) : undefined;

  async function handleFile(file: File): Promise<boolean> {
    if (!documentType) return false;
    if (!isAllowedContentType(file.type)) {
      toast.error('Choose a PDF, JPEG or PNG file');
      return false;
    }

    const values = await form.validateFields().catch(() => null);
    if (!values) return false; // the form itself now shows which fields need attention

    const uploaded = await onUpload(
      {
        documentTypeId: documentType.documentTypeId,
        file,
        number: values.number,
        issuer: values.issuer,
        issueDate: values.issueDate.format('YYYY-MM-DD'),
        expiryDate: values.expiryDate.format('YYYY-MM-DD'),
      },
      isRenewal,
    );
    if (uploaded) {
      form.resetFields();
      onClose();
    }
    return false;
  }

  return (
    <Modal
      open={open}
      title={documentType ? `${isRenewal ? 'Renew' : 'Add'} ${documentType.documentTypeName}` : 'Add document'}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Form<UploadFormValues> form={form} layout="vertical" requiredMark={false} disabled={uploading} initialValues={initialValues}>
        <ComplianceDocumentFormFields />
        <Upload
          accept={COMPLIANCE_DOCUMENT_CONTENT_TYPES.join(',')}
          showUploadList={false}
          disabled={uploading}
          beforeUpload={(file) => {
            void handleFile(file);
            return false;
          }}
        >
          <Button icon={<UploadCloud className="size-3.5" />} loading={uploading}>
            Choose file
          </Button>
        </Upload>
      </Form>
    </Modal>
  );
}
